/**
 * Tour interattivi guidati per ogni ruolo dell'app.
 * Usa react-joyride 3.x per evidenziare gli elementi reali della UI
 * con tooltip e spotlight contestuali.
 *
 * Fix iPad-portrait:
 *  - Prima di avviare un tour viene aperta automaticamente la sidebar mobile
 *    (se nascosta dietro hamburger) cliccando programmaticamente sul pulsante menu.
 *  - Se uno step ha un target NON trovato, il callback skippa al prossimo step
 *    (niente più tour bloccato in attesa di un selettore inesistente).
 *  - scrollToFirstStep + disableScrollParentFix per non rompere il layout fluid.
 *  - floaterProps.disableAnimation evita micro-glitch su Safari iOS.
 *  - Spotlight pad attivo: l'elemento highlightato sta sempre dentro lo schermo.
 *
 * Come funziona:
 *   - Ogni ruolo (admin/agent/warehouse/customer) ha la sua lista di TOUR.
 *   - Ogni tour ha più STEP (target CSS selector + contenuto JSX).
 *   - L'utente sceglie quale tour avviare dalla pagina "Guide & Tutorial".
 *   - I tour completati sono salvati in localStorage (`giotto_tours_done`).
 */
import React, { useState, useMemo, useCallback } from "react";
import { Joyride, STATUS, EVENTS } from "react-joyride";
import { Play, CheckCircle2, BookOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// ============ TOUR DEFINITIONS PER RUOLO ============
// Ogni tour ha: id, title, description, section, autoOpenSidebar?, steps[]
// Step: {target: CSS selector, content: JSX | string, placement?, disableBeacon?, fallbackToCenter?}

const TOURS = {
  // ============ ADMIN ============
  admin: [
    {
      id: "admin-overview",
      title: "🚀 Panoramica generale",
      description: "Conosci la sidebar e le 5 sezioni principali del pannello admin (~2 min).",
      section: "Inizia da qui",
      autoOpenSidebar: "admin",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Benvenuto nel pannello Admin di Giotto!</h3>
              <p className="text-sm">Questo tour ti accompagna passo-passo nei 5 gruppi della sidebar: <strong>Dashboard</strong>, <strong>Catalogo</strong>, <strong>Anagrafiche</strong>, <strong>Operatività</strong> e <strong>Sistema</strong>.</p>
              <p className="text-sm mt-2 text-stone-500">Su iPad/telefono apriamo automaticamente il menu laterale per te.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="sidebar-bi"]', content: "📊 Dashboard BI: KPI di vendite, fatturato, top clienti/prodotti, andamento giornaliero.", placement: "right" },
        { target: '[data-testid="sidebar-products"]', content: "🍖 Prodotti: l'anagrafica completa. Qui imposti ACQ, Bank, Marg, Extra e i prezzi per ogni listino.", placement: "right" },
        { target: '[data-testid="sidebar-customers"]', content: "👥 Clienti: ragione sociale, indirizzi consegna, plafond di credito e termini di pagamento.", placement: "right" },
        { target: '[data-testid="sidebar-orders"]', content: "📦 Ordini: tutti gli ordini in qualsiasi stato (in attesa, confermato, in preparazione, spedito).", placement: "right" },
        { target: '[data-testid="sidebar-statements"]', content: "💳 Scadenziario: documenti aperti/scaduti/pagati. Registra incassi e monitora l'esposizione.", placement: "right" },
        { target: '[data-testid="sidebar-payment-terms"]', content: "⚙️ Termini Pagamento: configura termini personalizzati (Anticipato, 30/60/90gg DF, 30/60gg FM).", placement: "right" },
        { target: '[data-testid="sidebar-tutorials"]', content: "🎬 Guide & Tutorial: sei qui ora! Avvia i tour quando vuoi, anche più volte.", placement: "right" },
      ],
    },
    {
      id: "admin-products",
      title: "🍖 Gestione Prodotti",
      description: "Come creare prodotti, capire la formula prezzo e gestire i listini (~3 min).",
      section: "Catalogo",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Gestione Prodotti</h3>
              <p className="text-sm">Naviga su <strong>Catalogo → Prodotti</strong> dalla sidebar PRIMA di avviare questo tour. Poi torna qui e clicca &quot;Avvia tour&quot;.</p>
              <p className="text-sm mt-2">Vedrai i campi del form prodotto evidenziati uno per uno.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="new-product-btn"]', content: "➕ Nuovo: apre il form di creazione prodotto.", placement: "bottom" },
        { target: '[data-testid="product-acq-input"]', content: "💰 ACQ: costo di acquisto al fornitore in €. È la base del calcolo prezzo.", placement: "top" },
        { target: '[data-testid="product-bank-input"]', content: "🏦 Bank: percentuale additiva sul costo (es. 1,5% = 0,15€ su ACQ 10€).", placement: "top" },
        { target: '[data-testid="product-marg-input"]', content: "📐 Marg: margine sul prezzo di vendita. ACQ 10€ con Marg 20% → 10 / (1 - 0,20) = 12,50€.", placement: "top" },
        { target: '[data-testid="product-extra-input"]', content: "➕ Extra: costo fisso aggiuntivo in € (lavorazione, packaging, ecc.).", placement: "top" },
        { target: '[data-testid="product-vat-input"]', content: "🧾 IVA in % (es. 10 = 10%). Sui clienti esteri viene azzerata automaticamente.", placement: "top" },
      ],
    },
    {
      id: "admin-customers",
      title: "👥 Anagrafica Clienti + Credit Control",
      description: "Aggiungere clienti, assegnare agente, impostare plafond e termini di pagamento (~3 min).",
      section: "Anagrafiche",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Clienti & Credit Control</h3>
              <p className="text-sm">Naviga su <strong>Anagrafiche → Clienti</strong> e apri il form (Nuovo o Modifica) PRIMA di avviare questo tour.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="cust-credit-limit"]', content: "🛡️ Plafond credito: esposizione massima consentita. Il magazzino vede un warning se l'aperto supera questo importo.", placement: "top" },
        { target: '[data-testid="cust-payment-terms"]', content: "💳 Termini di pagamento: scegli tra Anticipato, 30gg DF, 60gg FM ecc. Determina la due-date automatica dei documenti di vendita.", placement: "top" },
        { target: '[data-testid="cust-commission-override"]', content: "💸 Provvigione dedicata: se imposti un valore qui, sovrascrive quella dell'agente per questo cliente.", placement: "top" },
      ],
    },
    {
      id: "admin-scadenziario",
      title: "💳 Scadenziario / Estratto Conto",
      description: "Monitorare incassi, pagati e scaduti. Registrare un incasso (~2 min).",
      section: "Operatività",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Scadenziario</h3>
              <p className="text-sm">Naviga su <strong>Operatività → Scadenziario</strong> PRIMA di avviare il tour.</p>
              <p className="text-sm mt-2">I documenti sono generati <strong>automaticamente</strong> quando il magazzino spedisce un ordine.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="kpi-open"]', content: "💙 Aperto: totale € dei documenti non ancora pagati (esclusi gli scaduti).", placement: "bottom" },
        { target: '[data-testid="kpi-overdue"]', content: "🔴 Scaduto: documenti oltre la due-date. Chiama il cliente!", placement: "bottom" },
        { target: '[data-testid="kpi-partial"]', content: "🟡 Parziale: pagati solo parzialmente. Manca la differenza.", placement: "bottom" },
        { target: '[data-testid="kpi-paid"]', content: "🟢 Incassato: totale storico già incassato.", placement: "bottom" },
        { target: '[data-testid="filter-status"]', content: "🎛️ Filtri: cambia stato, cliente, agente, periodo. Trova subito il documento giusto.", placement: "bottom" },
      ],
    },
    {
      id: "admin-payment-terms",
      title: "⚙️ Termini di Pagamento",
      description: "Configurare termini di pagamento personalizzati per la tua attività (~1 min).",
      section: "Sistema",
      steps: [
        {
          target: "body",
          content: <div><h3 className="text-lg font-bold mb-2">Termini Pagamento</h3><p className="text-sm">Naviga su <strong>Sistema → Termini Pagamento</strong> PRIMA di avviare il tour.</p></div>,
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="add-payment-term-btn"]', content: "➕ Crea un termine personalizzato (es. '45 gg fine mese') che potrai poi assegnare ai clienti.", placement: "bottom" },
      ],
    },
  ],

  // ============ AGENT ============
  agent: [
    {
      id: "agent-overview",
      title: "🚀 Panoramica portale Agente",
      description: "Conosci il tuo portale: dashboard, ordini, clienti, scadenziario (~2 min).",
      section: "Inizia da qui",
      autoOpenSidebar: "agent",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Benvenuto Agente!</h3>
              <p className="text-sm">Dal tuo portale gestisci i tuoi clienti, crei ordini per loro e monitori le tue provvigioni.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="agent-nav-dashboard"]', content: "📊 Dashboard: KPI delle tue vendite, ordini recenti, clienti più attivi.", placement: "right" },
        { target: '[data-testid="agent-nav-new-order"]', content: "🛒 Nuovo Ordine: crea un nuovo ordine per un cliente in pochi click.", placement: "right" },
        { target: '[data-testid="agent-nav-customers"]', content: "👥 Clienti: la lista dei TUOI clienti assegnati.", placement: "right" },
        { target: '[data-testid="agent-nav-statements"]', content: "💳 Scadenziario: vedi documenti aperti/scaduti dei tuoi clienti. (Tu NON registri incassi, ma sai chi richiamare.)", placement: "right" },
        { target: '[data-testid="agent-nav-orders"]', content: "📦 Ordini: approva, modifica, rifiuta gli ordini in attesa.", placement: "right" },
        { target: '[data-testid="agent-nav-dedicated-offers"]', content: "✨ Offerte Dedicate: crea quotazioni multi-prodotto personalizzate per i tuoi clienti.", placement: "right" },
      ],
    },
    {
      id: "agent-new-order",
      title: "🛒 Creare un nuovo ordine",
      description: "Flusso completo: selezione cliente → catalogo → carrello → invio (~3 min).",
      section: "Operatività",
      steps: [
        {
          target: "body",
          content: <div><h3 className="text-lg font-bold mb-2">Nuovo Ordine</h3><p className="text-sm">Naviga su <strong>Nuovo ordine</strong> dalla sidebar PRIMA di avviare il tour.</p></div>,
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="customer-search-input"]', content: "1️⃣ Cerca il cliente per nome o P.IVA. Si caricano automaticamente indirizzo, listino e prezzi.", placement: "bottom" },
        { target: '[data-testid="product-search-input"]', content: "2️⃣ Cerca i prodotti del listino del cliente. Filtra per categoria o usa il tab Storico.", placement: "bottom" },
        { target: '[data-testid="cart-summary"]', content: "3️⃣ Il carrello mostra il totale aggiornato in tempo reale. Modifica quantità o U.M. (Kg/Pz/Ct).", placement: "left" },
        { target: '[data-testid="submit-order-btn"]', content: "4️⃣ Invia ordine: passa in stato 'Da approvare' fino a quando admin/agente lo conferma.", placement: "top" },
      ],
    },
  ],

  // ============ WAREHOUSE ============
  warehouse: [
    {
      id: "warehouse-overview",
      title: "🚀 Panoramica portale Magazzino",
      description: "Le 9 sezioni del portale magazzino e come si usano (~2 min).",
      section: "Inizia da qui",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Benvenuto Magazzino!</h3>
              <p className="text-sm">Il tuo portale ha 9 sezioni. Vediamo le più importanti.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="warehouse-nav-orders"], [data-testid="warehouse-nav-mobile-orders"]', content: "📦 Ordini: ordini in arrivo raggruppati per data. Click su una riga per evadere (peso effettivo, colli, DDT).", placement: "bottom" },
        { target: '[data-testid="warehouse-nav-statements"], [data-testid="warehouse-nav-mobile-statements"]', content: "💳 Scadenziario: anche tu puoi registrare incassi (non solo l'admin).", placement: "bottom" },
        { target: '[data-testid="warehouse-nav-customers"], [data-testid="warehouse-nav-mobile-customers"]', content: "👥 Clienti: puoi AGGIUNGERE nuovi clienti che vanno in anagrafica.", placement: "bottom" },
        { target: '[data-testid="warehouse-nav-pricelists"], [data-testid="warehouse-nav-mobile-pricelists"]', content: "📋 Listini: consultazione SOLO in lettura dei prezzi per cliente.", placement: "bottom" },
      ],
    },
    {
      id: "warehouse-order-flow",
      title: "📦 Evadere un ordine",
      description: "Dal click sull'ordine fino allo stato 'Spedito' con generazione automatica documento di vendita (~3 min).",
      section: "Operatività",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Evasione Ordine</h3>
              <p className="text-sm">Vai su <strong>Ordini</strong> e clicca un ordine in stato &quot;Confermato&quot; PRIMA di avviare il tour.</p>
              <p className="text-sm mt-2">Imparerai: modificare pesi/U.M., totale colli, status, riferimento DDT.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="warehouse-total-packages"]', content: "📦 Colli totali ordine: numero unico per tutto l'ordine. Apparirà sul PDF cliente e sui documenti di trasporto.", placement: "top" },
        { target: '[data-testid="warehouse-status-select"]', content: "🚚 Stato → Spedito: viene generato AUTOMATICAMENTE un documento di vendita (DV-2026-NNNN) con due-date dai termini pagamento.", placement: "top" },
      ],
    },
  ],

  // ============ CUSTOMER ============
  customer: [
    {
      id: "customer-overview",
      title: "🚀 Benvenuto nel portale Cliente",
      description: "Vedi i tuoi ordini, scadenze e fai nuovi ordini (~2 min).",
      section: "Inizia da qui",
      autoOpenSidebar: "customer",
      steps: [
        {
          target: "body",
          content: (
            <div>
              <h3 className="text-lg font-bold mb-2">Benvenuto!</h3>
              <p className="text-sm">Dal tuo portale puoi vedere i tuoi ordini storici, fare un nuovo ordine e controllare lo scadenziario.</p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        { target: '[data-testid="customer-nav-orders"]', content: "📦 I tuoi ordini: storico completo con stato (in lavorazione, spedito, consegnato).", placement: "bottom" },
        { target: '[data-testid="customer-nav-statements"]', content: "💳 Scadenziario: documenti aperti, pagati, scaduti. Ricorda: NON sono fatture fiscali (fa fede il Cassetto Fiscale).", placement: "bottom" },
        { target: '[data-testid="customer-nav-new-order"]', content: "🛒 Nuovo ordine: sfoglia il catalogo e aggiungi al carrello.", placement: "bottom" },
        { target: '[data-testid="customer-nav-offers"]', content: "✨ Offerte: promozioni speciali del periodo.", placement: "bottom" },
      ],
    },
    {
      id: "customer-new-order",
      title: "🛒 Fare un nuovo ordine",
      description: "Sfogliare catalogo, aggiungere al carrello, inviare (~2 min).",
      section: "Operatività",
      steps: [
        {
          target: "body",
          content: <div><h3 className="text-lg font-bold mb-2">Nuovo Ordine</h3><p className="text-sm">Naviga su <strong>Nuovo Ordine</strong> dal menu PRIMA di avviare il tour.</p></div>,
          placement: "center",
          disableBeacon: true,
        },
      ],
    },
  ],
};

// ============ LOCALSTORAGE HELPER ============
const DONE_KEY = "giotto_tours_done";
const getDone = () => {
  try { return JSON.parse(localStorage.getItem(DONE_KEY) || "[]"); } catch { return []; }
};
const markDone = (id) => {
  const done = getDone();
  if (!done.includes(id)) localStorage.setItem(DONE_KEY, JSON.stringify([...done, id]));
};

// Sui viewport piccoli (iPad portrait, telefono) la sidebar è dietro hamburger.
// Riapertura programmatica della sidebar via callback prima di ogni step.
const SIDEBAR_SELECTOR_PREFIXES = [
  "[data-testid=\"sidebar-",
  "[data-testid=\"agent-nav-",
  "[data-testid=\"customer-nav-",
  "[data-testid=\"warehouse-nav-",
];
const isSidebarTarget = (target) => {
  if (!target || typeof target !== "string") return false;
  return SIDEBAR_SELECTOR_PREFIXES.some(p => target.includes(p));
};

const isSmallViewport = () => typeof window !== "undefined" && window.innerWidth < 1024;

// Probe selector per stabilire se la sidebar è attualmente VISIBILE (qualsiasi elemento
// noto ha bbox > 0). Funziona indipendentemente da quale icona mostra l'hamburger.
const SIDEBAR_PROBE_BY_ROLE = {
  admin: '[data-testid="sidebar-tutorials"]',
  agent: '[data-testid="agent-nav-dashboard"]',
  customer: '[data-testid="customer-nav-orders"]',
  warehouse: '[data-testid="warehouse-nav-orders"]',
};
const MOBILE_TOGGLE_BY_ROLE = {
  admin: 'button.fixed.top-4.left-4',
  agent: '[data-testid="agent-mobile-menu-btn"]',
  customer: '[data-testid="customer-mobile-menu"]',
  // warehouse non ha sidebar collassata: la nav è sempre visibile come scroll orizzontale
  warehouse: null,
};

const sidebarIsVisible = (role) => {
  const probe = SIDEBAR_PROBE_BY_ROLE[role];
  if (!probe) return true;
  const nodes = document.querySelectorAll(probe);
  for (const el of nodes) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return true;
  }
  return false;
};

// Riapre la sidebar mobile cliccando il pulsante hamburger se la sidebar non è visibile.
// Su desktop (≥lg) non fa niente (la sidebar è sempre visibile).
const ensureSidebarOpen = (role) => {
  if (!isSmallViewport()) return;
  if (!role) return;
  if (sidebarIsVisible(role)) return;
  const sel = MOBILE_TOGGLE_BY_ROLE[role];
  if (!sel) return;
  try {
    const btn = document.querySelector(sel);
    if (!btn) return;
    // Verifica visibilità: bbox > 0 (più affidabile di offsetParent — gli elementi
    // position:fixed hanno SEMPRE offsetParent=null anche quando visibili).
    const r = btn.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) btn.click();
  } catch (_) { /* fail silently */ }
};

// ============ TOUR LIST COMPONENT (mostrato in TutorialsPage) ============
export const InteractiveTourList = ({ role }) => {
  const tours = TOURS[role] || [];
  const [running, setRunning] = useState(null);
  const [tick, setTick] = useState(0);
  const done = useMemo(() => getDone(), [tick]);

  const startTour = (tour) => {
    // Su viewport mobile/iPad: apri subito la sidebar prima di mostrare Joyride.
    // Su desktop la chiamata è no-op (la sidebar è già sempre visibile).
    ensureSidebarOpen(role);
    // Piccolo delay per montaggio Joyride + animazione apertura drawer su mobile.
    setTimeout(() => setRunning(tour), isSmallViewport() ? 350 : 50);
  };

  const finishTour = useCallback(() => {
    if (running) markDone(running.id);
    setRunning(null);
    setTick(t => t + 1);
  }, [running]);

  // Joyride callback:
  //  - STEP_BEFORE: prima di OGNI step, se viewport è mobile riapre la sidebar
  //    se è stata chiusa (per click su menu item, gesto utente, ecc.). In questo
  //    modo la sidebar resta aperta per tutta la durata del tour su iPad/telefono.
  //  - FINISHED/SKIPPED: termina il tour.
  //  - TARGET_NOT_FOUND: log + lascia che Joyride avanzi da solo (v3 lo fa).
  const handleJoyrideCallback = useCallback((data) => {
    const { status, type, step } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      finishTour();
      return;
    }

    if (type === EVENTS.STEP_BEFORE) {
      // Mantieni la sidebar mobile aperta SEMPRE durante il tour, non solo per gli
      // step che puntano alla sidebar — così l'utente la vede e capisce dove sta.
      // Se è già aperta, ensureSidebarOpen è no-op.
      ensureSidebarOpen(role);
      // Se il target è un selettore di sidebar e per qualche motivo non è ancora
      // visibile (animazione lenta del drawer), facciamo un secondo tentativo dopo
      // un breve delay — Joyride riterrà l'evento target_not_found e ricalcolerà.
      if (isSidebarTarget(step?.target) && !sidebarIsVisible(role)) {
        setTimeout(() => ensureSidebarOpen(role), 200);
      }
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      // Ultimo tentativo: probabilmente la sidebar non si è riaperta in tempo.
      console.warn("[InteractiveTour] target non trovato:", step?.target);
      ensureSidebarOpen(role);
    }
  }, [finishTour, role]);

  const grouped = tours.reduce((acc, t) => {
    const k = t.section || "Altro";
    (acc[k] = acc[k] || []).push(t);
    return acc;
  }, {});

  if (tours.length === 0) return null;

  return (
    <div className="space-y-5">
      <Card className="p-4 bg-gradient-to-r from-[#8A1C2A]/5 to-[#C5A059]/5 border-[#8A1C2A]/20">
        <div className="flex items-start gap-3">
          <BookOpen className="w-6 h-6 text-[#8A1C2A] shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-bold text-stone-900">🎯 Tour interattivi guidati</h2>
            <p className="text-sm text-stone-600 mt-1">
              Brevi tour passo-passo che <strong>evidenziano gli elementi reali della UI</strong>.
              Più rapidi di un video, sempre allineati alla schermata corrente. Funzionano anche su iPad e telefono.
            </p>
          </div>
        </div>
      </Card>

      {Object.keys(grouped).map(section => (
        <div key={section}>
          <h3 className="text-sm font-bold uppercase text-stone-500 tracking-wide mb-2">{section}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {grouped[section].map(t => {
              const isDone = done.includes(t.id);
              return (
                <Card key={t.id} className="p-4 bg-white border-stone-200 hover:border-[#8A1C2A] hover:shadow-md transition-all" data-testid={`tour-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-stone-900">{t.title}</h3>
                    {isDone && <Badge className="bg-green-100 text-green-800 border-green-200 text-[10px] shrink-0"><CheckCircle2 className="w-3 h-3 mr-1" />Fatto</Badge>}
                  </div>
                  <p className="text-sm text-stone-600 mt-1 mb-3 min-h-[40px]">{t.description}</p>
                  <Button onClick={() => startTour(t)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white w-full" size="sm" data-testid={`start-tour-${t.id}`}>
                    <Play className="w-3 h-3 mr-1" />{isDone ? "Rivedi tour" : "Avvia tour"}
                  </Button>
                  <p className="text-[10px] text-stone-400 mt-2">{t.steps.length} step</p>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {running && (
        <Joyride
          steps={running.steps.map(s => ({ ...s, disableBeacon: true }))}
          run={true}
          continuous
          showSkipButton
          showProgress
          disableOverlayClose
          disableScrollParentFix
          scrollToFirstStep
          scrollOffset={120}
          spotlightPadding={8}
          spotlightClicks={false}
          floaterProps={{ disableAnimation: true }}
          locale={{
            back: "← Indietro",
            close: "Chiudi",
            last: "✓ Fine",
            next: "Avanti →",
            skip: "Esci dal tour",
            open: "Apri",
          }}
          styles={{
            options: {
              primaryColor: "#8A1C2A",
              backgroundColor: "#fff",
              textColor: "#292524",
              arrowColor: "#fff",
              overlayColor: "rgba(0,0,0,0.55)",
              zIndex: 10000,
              spotlightShadow: "0 0 15px rgba(138, 28, 42, 0.4)",
            },
            tooltip: {
              borderRadius: 10,
              padding: 16,
              maxWidth: 360,
            },
            tooltipTitle: { fontSize: 16, fontWeight: 700 },
            buttonNext: {
              backgroundColor: "#8A1C2A",
              borderRadius: 6,
              padding: "8px 16px",
              fontSize: 13,
            },
            buttonBack: { color: "#8A1C2A", marginRight: 8 },
            buttonSkip: { color: "#78716c" },
            buttonClose: { display: "none" }, // forzo uso "Esci dal tour"
          }}
          callback={handleJoyrideCallback}
        />
      )}
    </div>
  );
};

export default InteractiveTourList;
